/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import axios from 'axios';
export const useCustomFieldsStore = defineStore('customFieldsStore', {
  state: () => ({
    customFields: [],
    loading: false,
    saving: false,
    error: null
  }),
  getters: {
    activeCustomFields: state => {
      if (!Array.isArray(state.customFields)) return [];
      return state.customFields.filter(f => f.active);
    },
    inactiveCustomFields: state => {
      if (!Array.isArray(state.customFields)) return [];
      return state.customFields.filter(f => !f.active);
    },
    clientFields: state => {
      if (!Array.isArray(state.customFields)) return [];
      return state.customFields.filter(f => f.showOnClient);
    },
    matterFields: state => {
      if (!Array.isArray(state.customFields)) return [];
      return state.customFields.filter(f => f.showOnMatter);
    },
    userFields: state => {
      if (!Array.isArray(state.customFields)) return [];
      return state.customFields.filter(f => f.showOnAccount);
    }
  },
  actions: {
    /**
     * List all custom fields
     */
    async listCustomFields() {
      this.loading = true;
      this.error = null;
      try {
        const {
          data
        } = await axios.get('/customfields');
        // Handle both array and object responses
        const fields = Array.isArray(data) ? data : data.customFields || [];
        this.customFields = fields;
        return fields;
      } catch (error) {
        this.error = error.response && error.response.data && error.response.data.message || error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },
    /**
     * Get a single custom field by ID
     */
    async getCustomField(id) {
      this.error = null;
      try {
        const {
          data
        } = await axios.get(`/customfields/${id}`);
        return data;
      } catch (error) {
        this.error = error.response && error.response.data && error.response.data.message || error.message;
        throw error;
      }
    },
    /**
     * Create a new custom field
     */
    async createCustomField(customField) {
      this.saving = true;
      this.error = null;
      try {
        const {
          data
        } = await axios.post('/customfields', customField);
        // Ensure customFields is an array before pushing
        if (!Array.isArray(this.customFields)) {
          this.customFields = [];
        }
        this.customFields.push(data);
        return data;
      } catch (error) {
        this.error = error.response && error.response.data && error.response.data.message || error.message;
        throw error;
      } finally {
        this.saving = false;
      }
    },
    /**
     * Update an existing custom field
     */
    async updateCustomField(id, customField) {
      this.saving = true;
      this.error = null;
      try {
        const {
          data
        } = await axios.put(`/customfields/${id}`, customField);
        // Ensure customFields is an array before updating
        if (!Array.isArray(this.customFields)) {
          this.customFields = [];
        }
        const index = this.customFields.findIndex(f => f.id === id);
        if (index !== -1) {
          this.customFields[index] = data;
        }
        return data;
      } catch (error) {
        this.error = error.response && error.response.data && error.response.data.message || error.message;
        throw error;
      } finally {
        this.saving = false;
      }
    },
    /**
     * Delete a custom field
     */
    async deleteCustomField(id) {
      this.saving = true;
      this.error = null;
      try {
        await axios.delete(`/customfields/${id}`);
        // Ensure customFields is an array before filtering
        if (!Array.isArray(this.customFields)) {
          this.customFields = [];
        }
        this.customFields = this.customFields.filter(f => f.id !== id);
      } catch (error) {
        this.error = error.response && error.response.data && error.response.data.message || error.message;
        throw error;
      } finally {
        this.saving = false;
      }
    },
    /**
     * Get usage count for a custom field (how many entities have values)
     */
    async getCustomFieldUsage(id) {
      try {
        const {
          data
        } = await axios.get(`/customfields/${id}/summary`);
        return data;
      } catch (error) {
        this.error = error.response && error.response.data && error.response.data.message || error.message;
        throw error;
      }
    },
    /**
     * Add or update dropdown option
     */
    async saveDropdownOption(fieldId, option) {
      this.saving = true;
      this.error = null;
      try {
        const url = option.id ? `/customfields/${fieldId}/options/${option.id}` : `/customfields/${fieldId}/options`;
        const method = option.id ? 'put' : 'post';
        const {
          data
        } = await axios[method](url, option);
        return data;
      } catch (error) {
        this.error = error.response && error.response.data && error.response.data.message || error.message;
        throw error;
      } finally {
        this.saving = false;
      }
    },
    /**
     * Delete dropdown option
     */
    async deleteDropdownOption(fieldId, optionId) {
      this.saving = true;
      this.error = null;
      try {
        await axios.delete(`/customfields/${fieldId}/options/${optionId}`);
      } catch (error) {
        this.error = error.response && error.response.data && error.response.data.message || error.message;
        throw error;
      } finally {
        this.saving = false;
      }
    }
  }
});